import { LotePedidoResponse } from '../../../../types/lotePedido/lotePedidoResponse';
import { CampanhaPedidoResgateResponse } from '../../../../types/campanha/campanhaPedidoResgateResponse';
import { GerarPixResponse } from '../../../../types/lotePedido/gerarPixResponse';

function formatCurrency(value: number | string | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateBR(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getStatusLabel(statusId: number | undefined): string {
  if (statusId === 1) return 'Solicitado';
  if (statusId === 2) return 'Aprovado';
  if (statusId === 3) return 'Não Aprovada';
  if (statusId === 4) return 'Pago/Entregue';
  if (statusId === 5) return 'Em Espera';
  if (statusId === 6) return 'Cancelado';
  if (statusId === 7) return 'Lote Alocado';
  if (statusId === 8) return 'Erro no Pagamento';
  return String(statusId ?? '-');
}

function getLoteStatusLabel(statusId: number | undefined): string {
  if (statusId === 1) return 'Pendente';
  if (statusId === 2) return 'Processando';
  if (statusId === 3) return 'Pago';
  if (statusId === 4) return 'Excluido';
  if (statusId === 5) return 'Parcialmente Pago';
  return String(statusId ?? '-');
}

function buildPaymentHtml(pixData: GerarPixResponse | null): string {
  if (!pixData) return '';

  return `<div class="payment-section"><div class="section-title">Pagamento</div><div class="payment-grid">
    <div class="payment-block payment-block-pix">
      <img src="data:image/png;base64,${pixData.QrCode}" alt="QR Code PIX" class="qr-image" />
      <div class="payment-block-title">PIX</div>
    </div>
  </div></div>`;
}

export function buildResumoHtml(
  lote: LotePedidoResponse,
  users: CampanhaPedidoResgateResponse[],
  pixData?: GerarPixResponse | null
): string {
  const columnCount = users.length > 60 ? 3 : users.length > 30 ? 2 : 1;
  const fontSize = users.length > 60 ? '7pt' : users.length > 30 ? '8pt' : '9pt';

  const userRows = users.map(function buildRow(user, index) {
    return `<tr>
      <td>${index + 1}</td>
      <td>${user.UsuarioNome || user.Usuario || '-'}</td>
      <td style="text-align:right">${formatCurrency(user.ValorOriginal)}</td>
      <td>${getStatusLabel(user.CampanhaPedidoResgateStatusID ?? user.Status)}</td>
    </tr>`;
  }).join('');

  const SERVICE_FEE = 0.22;
  const valorTaxa = parseFloat(lote.ValorTaxa) || 0;
  const hasServiceFee = valorTaxa <= SERVICE_FEE;
  const serviceFee = hasServiceFee ? SERVICE_FEE : 0;
  const valorTotal = (parseFloat(lote.ValorTotal) || 0) + serviceFee;

  const serviceFeeRow = hasServiceFee
    ? `<div class="summary-row"><span class="summary-label">Taxa de serviço:</span><span class="summary-value">${formatCurrency(serviceFee)}</span></div>`
    : '';

  const paymentHtml = buildPaymentHtml(pixData ?? null);

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Resumo do Lote - ${lote.Nome}</title>
  <style>
    @page {
      size: A4;
      margin: 12mm;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: ${fontSize};
      color: #333;
      line-height: 1.3;
    }
    .page-title {
      text-align: center;
      font-size: 14pt;
      font-weight: 700;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #333;
    }
    .header-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px 16px;
      margin-bottom: 12px;
      padding: 8px;
      background: #f8f8f8;
      border-radius: 4px;
    }
    .header-item {
      display: flex;
      gap: 4px;
    }
    .header-label {
      font-weight: 600;
      color: #555;
    }
    .header-value {
      color: #222;
    }
    .section-title {
      font-size: 10pt;
      font-weight: 600;
      margin: 8px 0 4px;
      padding-bottom: 2px;
      border-bottom: 1px solid #ccc;
    }
    .user-list {
      column-count: ${columnCount};
      column-gap: 12px;
      margin-bottom: 12px;
    }
    .user-list table {
      width: 100%;
      border-collapse: collapse;
      break-inside: avoid;
    }
    .user-list th,
    .user-list td {
      padding: 2px 4px;
      border-bottom: 1px solid #eee;
      text-align: left;
    }
    .user-list th {
      background: #f0f0f0;
      font-weight: 600;
      font-size: ${fontSize};
    }
    .user-list tr:nth-child(even) {
      background: #fafafa;
    }
    .finance-row {
      display: flex;
      gap: 12px;
      align-items: stretch;
      margin-top: 8px;
      break-inside: avoid;
    }
    .finance-col {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }
    .summary {
      flex: 1;
      margin-top: 4px;
      padding: 10px;
      border: 2px solid #333;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .summary-row + .summary-row {
      margin-top: 2px;
    }
    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 3px 0;
    }
    .summary-row.total {
      font-size: 11pt;
      font-weight: 700;
      border-top: 1px solid #333;
      padding-top: 6px;
      margin-top: 4px;
    }
    .summary-label {
      color: #555;
    }
    .summary-value {
      font-weight: 600;
    }
    .payment-section {
      flex-shrink: 0;
      min-width: 200px;
      display: flex;
      flex-direction: column;
    }
    .payment-grid {
      flex: 1;
      display: flex;
      justify-content: center;
      gap: 12px;
      align-items: stretch;
      margin-top: 4px;
    }
    .payment-block {
      flex: 1;
      width: 100%;
    }
    .payment-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px;
      border: 1px solid #ddd;
      border-radius: 6px;
    }
    .payment-block-title {
      font-size: 9pt;
      font-weight: 600;
      color: #333;
    }
    .qr-image {
      width: 100%;
      max-width: 140px;
      height: auto;
    }
    .footer {
      margin-top: 12px;
      text-align: center;
      font-size: 7pt;
      color: #999;
    }
  </style>
</head>
<body>
  <div class="page-title">Resumo do Lote</div>

  <div class="header-grid">
    <div class="header-item">
      <span class="header-label">Lote:</span>
      <span class="header-value">${lote.Nome}</span>
    </div>
    <div class="header-item">
      <span class="header-label">Data:</span>
      <span class="header-value">${formatDateBR(lote.RealizadoEm)}</span>
    </div>
    <div class="header-item">
      <span class="header-label">Numero:</span>
      <span class="header-value">${lote.NumeroLote || '-'}</span>
    </div>
    <div class="header-item">
      <span class="header-label">Status:</span>
      <span class="header-value">${getLoteStatusLabel(lote.CampanhaPedidoResgatePagamentoStatusID)}</span>
    </div>
    <div class="header-item">
      <span class="header-label">Realizado por:</span>
      <span class="header-value">${lote.RealizadoPor || lote.Usuario || '-'}</span>
    </div>
    <div class="header-item">
      <span class="header-label">Usuarios:</span>
      <span class="header-value">${lote.QtdeUsuarios}</span>
    </div>
  </div>

  <div class="section-title">Usuarios</div>
  <div class="user-list">
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Usuario</th>
          <th style="text-align:right">Valor</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${userRows}
      </tbody>
    </table>
  </div>

  <div class="finance-row">
    <div class="finance-col">
      <div class="section-title">Resumo Financeiro</div>
      <div class="summary">
        <div class="summary-row">
          <span class="summary-label">Valor Original:</span>
          <span class="summary-value">${formatCurrency(lote.ValorOriginal)}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Taxa:</span>
          <span class="summary-value">${formatCurrency(lote.ValorTaxa)}</span>
        </div>
        ${serviceFeeRow}
        <div class="summary-row total">
          <span class="summary-label">Valor Total:</span>
          <span class="summary-value">${formatCurrency(valorTotal)}</span>
        </div>
      </div>
    </div>
    ${paymentHtml}
  </div>

  <div class="footer">
    Documento gerado em ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR')}
  </div>
</body>
</html>`;
}
